import { Dialog } from '@angular/cdk/dialog';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { firstValueFrom } from 'rxjs';
import {
  StaffQrPrintCopiesDialogComponent,
  type StaffQrPrintCopiesDialogData,
} from '../scan/staff-qr-print-copies-dialog.component';
import {
  buildStaffQrLabelText,
  type StaffQrLabelEntityKind,
} from './staff-qr-label-type.util';

export interface PrintStaffEntityQrOptions {
  entityId: string;
  entityKind: StaffQrLabelEntityKind;
  caption?: string;
  printer: NiimbotPrinterService;
  translate: TranslateService;
  notification: NotSystemNotificationService;
}

export interface PrintStaffCellQrOptions extends PrintStaffEntityQrOptions {
  dialog: Dialog;
}

async function promptLabelCopies(
  dialog: Dialog,
  defaultCopies: number,
): Promise<number | null> {
  const ref = dialog.open<number | undefined, StaffQrPrintCopiesDialogData>(
    StaffQrPrintCopiesDialogComponent,
    {
      data: { defaultCopies },
    },
  );
  const result = await firstValueFrom(ref.closed);
  if (result === undefined) {
    return null;
  }
  return result;
}

export async function printStaffEntityQr(
  options: PrintStaffEntityQrOptions,
): Promise<void> {
  const id = options.entityId.trim();
  if (!id) {
    return;
  }

  const label = buildStaffQrLabelText(
    options.translate,
    options.entityKind,
    options.caption,
  );

  if (Capacitor.isNativePlatform()) {
    try {
      await options.printer.printQrCodeWithSavedLabelPreset(id, undefined, label);
      options.notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [
          options.translate.instant('admin.moverInventory.printQrSuccess'),
        ],
      });
    } catch {
      options.notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [options.translate.instant('admin.moverInventory.printQrError')],
      });
    }
    return;
  }

  try {
    await navigator.clipboard.writeText(id);
  } catch {
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [options.translate.instant('common.clipboardCopyError')],
    });
    return;
  }

  options.notification.addToQueue({
    id: Date.now().toString(),
    type: 'success',
    text: [options.translate.instant('admin.moverInventory.printQrCopied')],
  });
}

export interface PrintStaffCellQrBatchOptions {
  entityIds: string[];
  caption?: string;
  printer: NiimbotPrinterService;
  translate: TranslateService;
  notification: NotSystemNotificationService;
  dialog: Dialog;
}

export async function printStaffCellQrBatch(
  options: PrintStaffCellQrBatchOptions,
): Promise<void> {
  const ids = options.entityIds.map((id) => id.trim()).filter(Boolean);
  if (ids.length === 0) {
    return;
  }

  if (ids.length === 1) {
    const singleId = ids[0];
    if (!singleId) {
      return;
    }
    await printStaffCellQr({
      entityId: singleId,
      entityKind: 'cell',
      caption: options.caption,
      printer: options.printer,
      translate: options.translate,
      notification: options.notification,
      dialog: options.dialog,
    });
    return;
  }

  if (!Capacitor.isNativePlatform()) {
    try {
      await navigator.clipboard.writeText(ids.join('\n'));
    } catch {
      options.notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [options.translate.instant('common.clipboardCopyError')],
      });
      return;
    }

    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        options.translate.instant('admin.warehouses.cells.printQrCopiedMany', {
          count: ids.length,
        }),
      ],
    });
    return;
  }

  const defaultCopies = options.printer.getSavedLabelsPerBatch();
  const copies = await promptLabelCopies(options.dialog, defaultCopies);
  if (copies === null) {
    return;
  }

  options.printer.saveLabelsPerBatch(copies);

  const label = buildStaffQrLabelText(
    options.translate,
    'cell',
    options.caption,
  );

  try {
    for (const id of ids) {
      await options.printer.printQrCodeWithSavedLabelPreset(id, copies, label);
    }
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        options.translate.instant('admin.warehouses.cells.printQrSuccessMany', {
          count: ids.length,
        }),
      ],
    });
  } catch {
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [options.translate.instant('admin.moverInventory.printQrError')],
    });
  }
}

export async function printStaffCellQr(
  options: PrintStaffCellQrOptions,
): Promise<void> {
  const id = options.entityId.trim();
  if (!id) {
    return;
  }

  if (!Capacitor.isNativePlatform()) {
    await printStaffEntityQr(options);
    return;
  }

  const defaultCopies = options.printer.getSavedLabelsPerBatch();
  const copies = await promptLabelCopies(options.dialog, defaultCopies);
  if (copies === null) {
    return;
  }

  options.printer.saveLabelsPerBatch(copies);

  const label = buildStaffQrLabelText(
    options.translate,
    'cell',
    options.caption,
  );

  try {
    await options.printer.printQrCodeWithSavedLabelPreset(id, copies, label);
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        options.translate.instant('admin.moverInventory.printQrSuccess'),
      ],
    });
  } catch {
    options.notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [options.translate.instant('admin.moverInventory.printQrError')],
    });
  }
}
